import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonProps,
  ExpandableSection,
  FormField,
  InputProps,
  Modal,
  RadioGroup,
  SelectProps,
  SpaceBetween,
  Tabs,
} from "@amzn/awsui-components-react";
import {
  OmsPresetRequest,
  OmsPresetScope,
  OmsProduct,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import useOmsExport, { restoredOmsExportMode } from "../../hooks/useOmsExport";
import useOmsFilterCriteria, {
  OmsFilterDraft,
} from "../../hooks/useOmsFilterCriteria";
import useOmsModalHeight from "../../hooks/useOmsModalHeight";
import useOmsPresets, {
  presetErrorMessage,
  presetValidationSourceUnavailable,
  useOmsExportCapabilities,
} from "../../hooks/useOmsPresets";
import OMSColumns, {
  columnSelectionError,
  OmsColumnsDraft,
} from "./OMSColumns";
import OMSExportPanel, { OMSExportActions } from "./OMSExportPanel";
import OMSExportSource from "./OMSExportSource";
import OMSPresetToolbar from "./OMSPresetToolbar";
import OMSPresetContext from "./OMSPresetContext";
import OMSPresetChanges from "./OMSPresetChanges";
import { OMSPresetManager, OMSPresetSave } from "./OMSPresetEditor";
import OMSRecordFilters, { AppliedOmsFilters } from "./OMSRecordFilters";
import {
  applyPreset,
  OmsConfiguration,
  presetIsEdited,
} from "./omsPresetEditing";

export default function OMSExportDialog({
  product,
  productLabel,
  report,
  availableReport,
  onClose,
  draft,
  onSaveDraft,
}: {
  product: OmsProduct;
  productLabel: string;
  report: OmsReport;
  availableReport: OmsReport;
  onClose: () => void;
  draft?: OmsFilterDraft;
  onSaveDraft: (draft: OmsFilterDraft) => void;
}) {
  const [mode, setMode] = useState<"full" | "filtered">(() => {
    if (draft) return draft.tab === "filtered" ? "filtered" : "full";
    return restoredOmsExportMode(product, report);
  });
  const [focusFilters, setFocusFilters] = useState(false);
  const [focusColumns, setFocusColumns] = useState(false);
  const [workspace, setWorkspace] = useState<"rows" | "columns">(
    draft?.workspace || "rows"
  );
  const [columns, setColumns] = useState<OmsColumnsDraft>(
    draft?.columns || { strategy: "all", names: [] }
  );
  const [applied, setApplied] = useState(draft?.appliedPreset);
  const [undo, setUndo] = useState<OmsConfiguration>();
  const [announcement, setAnnouncement] = useState("");
  const [editor, setEditor] = useState<
    NonNullable<OmsFilterDraft["presetEditor"]>
  >(draft?.presetEditor || { view: "export", name: "", scope: "columns" });
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [saveValidation, setSaveValidation] = useState("");
  const saveRef = useRef<ButtonProps.Ref>(null);
  const reviewRef = useRef<ButtonProps.Ref>(null);
  const manageRef = useRef<ButtonProps.Ref>(null);
  const selectRef = useRef<SelectProps.Ref>(null);
  const nameRef = useRef<InputProps.Ref>(null);
  const editGeneration = useRef(0);
  const capabilities = useOmsExportCapabilities();
  const presetsEnabled = Boolean(capabilities.data?.presets);
  const columnsEnabled = Boolean(capabilities.data?.columnSelection);
  const presets = useOmsPresets(product, presetsEnabled, draft?.presetRequest);
  const full = useOmsExport(
    product,
    draft?.report || report,
    "full",
    availableReport,
    !draft?.editing,
    draft?.request?.mode === "full" ? draft.request : undefined
  );
  const filtered = useOmsExport(
    product,
    draft?.report || report,
    "filtered",
    availableReport,
    !draft?.editing,
    draft?.request?.mode === "filtered" ? draft.request : undefined
  );
  const current = mode === "full" ? full : filtered;
  const source = current.sourceReport;
  const criteria = useOmsFilterCriteria(source, filtered.job.data, draft);
  const active = Boolean(current.jobId) || current.create.isLoading;
  const accepted = current.job.data;
  const restored = useRef<string>();
  useEffect(() => {
    if (!accepted || restored.current === accepted.jobId) return;
    restored.current = accepted.jobId;
    setColumns({
      strategy: accepted.outputColumnStrategy || "all",
      names: accepted.outputColumns || [...source.columns],
    });
  }, [accepted, source.columns]);
  const configuration: OmsConfiguration = {
    mode,
    criteria: criteria.criteria,
    operation: criteria.operation,
    columns,
    appliedPreset: applied,
  };
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;
  const definitionEdited =
    (editor.view === "edit" || (editor.view === "save" && editor.updating)) &&
    (editor.name.trim().normalize("NFC") !== applied?.name ||
      editor.scope !== applied?.scope);
  const edited = Boolean(
    applied && (presetIsEdited(applied, configuration) || definitionEdited)
  );
  const columnError = columnSelectionError(columns, source);
  const validFilters = mode === "filtered" && criteria.valid;
  const previousRequest = current.matchesPreviousRequest({
    filters: mode === "full" ? [] : criteria.filters,
    operation: mode === "full" ? "and" : criteria.operation,
    outputColumns: columns.strategy === "selected" ? columns.names : undefined,
  });
  const validColumns =
    !columnError &&
    (columnsEnabled || columns.strategy === "all" || previousRequest);
  const preparing =
    current.create.isLoading ||
    (Boolean(current.jobId) &&
      !current.expired &&
      !current.unavailable &&
      !current.job.isError &&
      (!accepted || accepted.status === "PREPARING"));
  const { height, contentRef, titleRef, footerRef, paneRef, paneMaxHeight } =
    useOmsModalHeight(workspace === "columns" ? 600 : 480);
  const editingPreset = editor.view === "edit";

  function changed() {
    editGeneration.current += 1;
    setUndo(undefined);
    setAnnouncement("");
  }
  function restore(next: OmsConfiguration, nextSource = source) {
    // A reversible preset edit must retain an uncertain request's retry identity.
    if (full.jobId || full.sourceReport.snapshotId !== nextSource.snapshotId)
      full.editSettings(nextSource);
    if (
      filtered.jobId ||
      filtered.sourceReport.snapshotId !== nextSource.snapshotId
    )
      filtered.editSettings(nextSource);
    setMode(next.mode);
    criteria.replace(next.criteria, next.operation);
    setColumns(next.columns);
    setApplied(next.appliedPreset);
  }
  const selectPreset = (id: string, editing = false) => {
    const generation = editGeneration.current;
    presets.read
      .mutateAsync(id)
      .then((preset) => {
        if (generation !== editGeneration.current) {
          setAnnouncement(
            "Settings changed while the preset loaded. Choose it again to apply."
          );
          return;
        }
        const previous = configurationRef.current;
        const next = applyPreset(preset, previous, source);
        restore(next);
        setUndo(previous);
        setAnnouncement(
          editing
            ? `Editing ${preset.name}. Review changes before saving.`
            : `Applied ${preset.name}.`
        );
        if (editing)
          setEditor({
            view: "edit",
            name: preset.name,
            scope: preset.scope,
            updating: true,
            previous,
            previousReport: source,
            previousWorkspace: workspace,
            cancelTo: editor.view === "manage" ? "manage" : "export",
          });
        if (columnSelectionError(next.columns, source)) {
          setWorkspace("columns");
          setFocusColumns(true);
        } else if (preset.scope !== "columns") {
          setWorkspace("rows");
          setFocusFilters(true);
        } else {
          setWorkspace("columns");
          setFocusColumns(true);
        }
      })
      .catch(() => undefined);
  };
  const openSave = (
    updating = false,
    returnTo: "save" | "actions" | "edit" = "save"
  ) => {
    presets.change.reset();
    setSaveValidation("");
    setSaveAttempted(false);
    let scope: OmsPresetScope = "columns";
    let name = "";
    if (updating && applied)
      scope = editor.view === "edit" ? editor.scope : applied.scope;
    else if (validFilters && validColumns) scope = "both";
    if (updating && applied)
      name = editor.view === "edit" ? editor.name : applied.name;
    setEditor({
      ...editor,
      view: "save",
      name,
      scope,
      updating,
      returnTo,
    });
  };
  const backToExport = () => {
    setEditor({
      ...editor,
      view: editor.returnTo === "edit" ? "edit" : "export",
    });
    window.setTimeout(() => {
      if (editor.returnTo === "actions") manageRef.current?.focus();
      else if (
        reviewRef.current &&
        (editor.returnTo === "edit" || editor.updating || applied)
      )
        reviewRef.current?.focus();
      else saveRef.current?.focus();
    }, 0);
  };
  const cancelEditing = () => {
    if (editor.previous)
      restore(editor.previous, editor.previousReport || source);
    if (editor.previousWorkspace) setWorkspace(editor.previousWorkspace);
    setEditor({
      view: editor.cancelTo || "export",
      name: "",
      scope: "columns",
    });
    setUndo(undefined);
    setAnnouncement("");
    window.setTimeout(() => selectRef.current?.focus(), 0);
  };
  const savePreset = () => {
    setSaveAttempted(true);
    const name = editor.name.trim().normalize("NFC");
    if (!name || Array.from(name).length > 80 || /\p{C}/u.test(name)) {
      setSaveValidation(
        "Enter a name of 1–80 characters without control characters."
      );
      nameRef.current?.focus();
      return;
    }
    if (
      (editor.scope !== "columns" && !validFilters) ||
      (editor.scope !== "filters" && !validColumns)
    ) {
      setSaveValidation("Correct the settings included in this preset.");
      return;
    }
    const body: OmsPresetRequest = {
      name,
      scope: editor.scope,
      validationSnapshotId: source.snapshotId,
      ...(editor.scope !== "columns"
        ? { filters: criteria.filters, operation: criteria.operation }
        : {}),
      ...(editor.scope !== "filters"
        ? {
            columns:
              columns.strategy === "all"
                ? { strategy: "all" }
                : { strategy: "selected", names: columns.names },
          }
        : {}),
    };
    if (new Blob([JSON.stringify(body)]).size > 16 * 1024) {
      setSaveValidation(
        "These settings exceed the 16 KiB preset limit. You can still export them without saving."
      );
      return;
    }
    setSaveValidation("");
    presets.change
      .mutateAsync({
        action: "save",
        body,
        existing: editor.updating ? applied : undefined,
      })
      .then((result) => {
        if (result.preset) setApplied(result.preset);
        setAnnouncement(`Saved ${name}.`);
        setUndo(undefined);
        setEditor({ view: "export", name: "", scope: "columns" });
        window.setTimeout(() => {
          if (active) saveRef.current?.focus();
          else selectRef.current?.focus();
        }, 0);
      })
      .catch(() => nameRef.current?.focus());
  };
  const close = () => {
    const retryRequest = current.requestIdentity();
    onSaveDraft({
      snapshotId: source.snapshotId,
      report: source,
      editing: !active,
      criteria: criteria.criteria,
      operation: criteria.operation,
      tab: mode,
      columns,
      workspace,
      appliedPreset: applied,
      presetEditor: editor,
      request: retryRequest ? { mode, ...retryRequest } : undefined,
      presetRequest: presets.changeIdentity(),
    });
    onClose();
  };
  const editableCriteria = {
    ...criteria,
    update: (...args: Parameters<typeof criteria.update>) => {
      changed();
      criteria.update(...args);
    },
    setOperator: (...args: Parameters<typeof criteria.setOperator>) => {
      changed();
      criteria.setOperator(...args);
    },
    setColumn: (...args: Parameters<typeof criteria.setColumn>) => {
      changed();
      criteria.setColumn(...args);
    },
    setOperation: (...args: Parameters<typeof criteria.setOperation>) => {
      changed();
      criteria.setOperation(...args);
    },
    add: () => {
      changed();
      criteria.add();
    },
    remove: (index: number) => {
      changed();
      criteria.remove(index);
    },
    clear: () => {
      changed();
      criteria.clear();
    },
  };
  const saveStatus = (
    presets.change.error as { response?: { status?: number } }
  )?.response?.status;
  let summaryColumns =
    columns.strategy === "all" ? source.columns : columns.names;
  let summaryStrategy = columns.strategy;
  if (active && accepted) {
    summaryColumns = accepted.outputColumns || source.columns;
    summaryStrategy = accepted.outputColumnStrategy || "all";
  }
  const columnSummary =
    summaryStrategy === "all"
      ? `All columns (${summaryColumns.length})`
      : `${summaryColumns.length} selected columns`;
  let title = `Export ${productLabel} report`;
  if (editor.view === "save")
    title = editor.updating
      ? `Review preset changes · ${applied?.name || productLabel}`
      : `Save preset · ${productLabel}`;
  else if (editingPreset)
    title = `Edit preset · ${applied?.name || productLabel}`;
  else if (editor.view === "manage") title = `Manage presets · ${productLabel}`;

  return (
    <Modal
      visible
      onDismiss={close}
      header={<span ref={titleRef}>{title}</span>}
      size="xx-large"
      position="top"
      height={height}
      closeAriaLabel="Close report export"
      footer={
        <div ref={footerRef} className="oms-report-download__footer">
          {editor.view === "export" && (
            <>
              {!active && (
                <div
                  className="oms-export-summary"
                  role="group"
                  aria-label="Export settings"
                >
                  <Box fontWeight="bold">
                    {mode === "full"
                      ? "All rows"
                      : `${criteria.criteria.length} ${
                          criteria.criteria.length === 1 ? "filter" : "filters"
                        }`}
                    {" · "}
                    {columnSummary}
                  </Box>
                  <Box color="text-body-secondary" fontSize="body-s">
                    CSV in a ZIP
                  </Box>
                </div>
              )}
              <Button
                variant={current.jobId && preparing ? "primary" : "normal"}
                onClick={close}
              >
                Close
              </Button>
              {presetsEnabled && active && accepted?.status === "READY" && (
                <Button ref={saveRef} onClick={() => openSave()}>
                  Save as preset
                </Button>
              )}
              <OMSExportActions
                state={current}
                filters={mode === "full" ? [] : criteria.filters}
                operation={mode === "full" ? "and" : criteria.operation}
                outputColumns={
                  columns.strategy === "selected" ? columns.names : undefined
                }
                valid={
                  (mode === "full" || criteria.valid) &&
                  validColumns &&
                  !presets.read.isLoading
                }
                onConfigure={() => {
                  changed();
                  if (mode === "filtered") {
                    setWorkspace("rows");
                    setFocusFilters(true);
                  }
                }}
              />
            </>
          )}
          {editingPreset && (
            <>
              <Button onClick={cancelEditing}>Cancel editing</Button>
              <Button
                ref={reviewRef}
                variant="primary"
                disabled={presets.read.isLoading}
                onClick={() => openSave(true, "edit")}
              >
                Review and save
              </Button>
            </>
          )}
          {(editor.view === "save" || editor.view === "manage") && (
            <>
              <Button
                disabled={presets.change.isLoading}
                onClick={backToExport}
              >
                {editor.view === "save" ? "Back to settings" : "Back to export"}
              </Button>
              {editor.view === "save" && (
                <Button
                  variant="primary"
                  loading={presets.change.isLoading}
                  loadingText="Saving preset"
                  onClick={savePreset}
                  disabled={
                    (editor.updating && !edited) ||
                    (editor.scope !== "columns" && !validFilters) ||
                    (editor.scope !== "filters" && !validColumns)
                  }
                >
                  {editor.updating ? "Save changes" : "Save preset"}
                </Button>
              )}
            </>
          )}
        </div>
      }
    >
      <div ref={contentRef}>
        {editor.view === "save" && (
          <SpaceBetween size="m">
            {(saveValidation || presets.change.isError) && (
              <Alert
                type="error"
                header="Could not save preset"
                action={
                  saveStatus === 412 ||
                  (saveStatus === 404 &&
                    !presetValidationSourceUnavailable(
                      presets.change.error
                    )) ? (
                    <SpaceBetween direction="horizontal" size="xs">
                      {applied && (
                        <Button
                          onClick={() => {
                            selectPreset(applied.presetId);
                            backToExport();
                          }}
                        >
                          Reload preset
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          presets.change.reset();
                          setEditor({
                            ...editor,
                            updating: false,
                            name: `${editor.name.slice(0, 75)} copy`,
                          });
                        }}
                      >
                        Save as new
                      </Button>
                    </SpaceBetween>
                  ) : undefined
                }
              >
                {saveValidation || presetErrorMessage(presets.change.error)}
              </Alert>
            )}
            <OMSPresetSave
              name={editor.name}
              scope={editor.scope}
              onName={(name) => setEditor({ ...editor, name })}
              onScope={(scope: OmsPresetScope) =>
                setEditor({ ...editor, scope })
              }
              filterCount={criteria.filters.length}
              columnSummary={columnSummary}
              validFilters={validFilters}
              validColumns={validColumns}
              attempted={saveAttempted}
              nameRef={nameRef}
              disabled={presets.change.isLoading}
              reviewing={editor.updating}
            />
            {editor.updating && applied && (
              <OMSPresetChanges
                saved={applied}
                draft={configuration}
                name={editor.name}
                scope={editor.scope}
                report={source}
              />
            )}
          </SpaceBetween>
        )}
        {editor.view === "manage" && (
          <OMSPresetManager
            state={presets}
            onEdit={(id) => selectPreset(id, true)}
            onDeleted={(id) => {
              if (applied?.presetId === id) setApplied(undefined);
              setUndo(undefined);
              setAnnouncement(
                "Preset deleted. Applied settings are preserved."
              );
            }}
            onRenamed={(metadata) => {
              if (applied?.presetId === metadata.presetId)
                setApplied({ ...applied, ...metadata });
            }}
          />
        )}
        {(editor.view === "export" || editingPreset) && (
          <SpaceBetween size="m">
            <OMSExportSource
              report={source}
              availableReport={availableReport}
              accepted={active}
              compact={!active}
              onUseLatest={() => {
                changed();
                criteria.replace(
                  criteria.criteria.map((criterion) => {
                    const previousType =
                      source.columnTypes?.[criterion.column] || "string";
                    const nextType =
                      availableReport.columnTypes?.[criterion.column] ||
                      "string";
                    return availableReport.columns.includes(criterion.column) &&
                      previousType !== nextType
                      ? {
                          ...criterion,
                          valueNotice: `This field changed from ${previousType} to ${nextType}. Review its condition and value.`,
                        }
                      : criterion;
                  }),
                  criteria.operation
                );
                full.editSettings(availableReport);
                filtered.editSettings(availableReport);
                if (mode === "filtered") {
                  setWorkspace("rows");
                  setFocusFilters(true);
                }
                if (columns.strategy === "selected") {
                  setColumns({ ...columns, touched: true });
                  if (columnSelectionError(columns, availableReport)) {
                    setWorkspace("columns");
                    setFocusColumns(true);
                  }
                }
              }}
            />
            {(current.jobId || !current.create.isIdle) && (
              <OMSExportPanel state={current} mode={mode} />
            )}
            {presetsEnabled && !editingPreset && !active && (
              <OMSPresetToolbar
                state={presets}
                applied={applied}
                disabled={active}
                saveDisabled={active && accepted?.status !== "READY"}
                onApply={selectPreset}
                onSave={() => openSave()}
                onManage={() => {
                  presets.change.reset();
                  setEditor({
                    ...editor,
                    view: "manage",
                    returnTo: "actions",
                  });
                }}
                saveRef={saveRef}
                selectRef={selectRef}
                manageRef={manageRef}
              />
            )}
            {applied && !active && (
              <OMSPresetContext
                preset={applied}
                configuration={configuration}
                edited={edited}
                editing={editingPreset}
                name={editingPreset ? editor.name : applied.name}
                scope={editingPreset ? editor.scope : applied.scope}
                disabled={presets.read.isLoading}
                onReview={() => openSave(true)}
                onEdit={() => selectPreset(applied.presetId, true)}
                onSaveAsNew={() => openSave()}
                reviewRef={reviewRef}
                onUndo={
                  undo && !editingPreset
                    ? () => {
                        restore(undo);
                        setUndo(undefined);
                        setAnnouncement("Previous settings restored.");
                        selectRef.current?.focus();
                      }
                    : undefined
                }
              />
            )}
            {announcement &&
              !announcement.startsWith("Applied ") &&
              !announcement.startsWith("Editing ") && (
                <Alert
                  type="info"
                  dismissible
                  dismissAriaLabel="Dismiss preset notice"
                  onDismiss={() => setAnnouncement("")}
                >
                  {announcement}
                </Alert>
              )}
            {!active && (
              <>
                {source.rowCount === 0 && (
                  <Alert type="info" header="Empty report">
                    The export will contain headers only.
                  </Alert>
                )}
                {((mode === "filtered" &&
                  !criteria.valid &&
                  criteria.criteria.some((criterion) => criterion.column)) ||
                  (columnError && columns.touched)) && (
                  <Alert
                    type="warning"
                    header="Check your settings"
                    action={
                      <SpaceBetween direction="horizontal" size="s">
                        {mode === "filtered" && !criteria.valid && (
                          <Button
                            onClick={() => {
                              setWorkspace("rows");
                              setFocusFilters(true);
                            }}
                          >
                            Review filters
                          </Button>
                        )}
                        {columnError && (
                          <Button
                            onClick={() => {
                              setWorkspace("columns");
                              setFocusColumns(true);
                            }}
                          >
                            Review columns
                          </Button>
                        )}
                      </SpaceBetween>
                    }
                  >
                    Correct the highlighted filters or columns before exporting.
                  </Alert>
                )}
                <Tabs
                  ariaLabel="Report export settings"
                  activeTabId={columnsEnabled ? workspace : "rows"}
                  onChange={({ detail }) =>
                    setWorkspace(detail.activeTabId as "rows" | "columns")
                  }
                  tabs={[
                    {
                      id: "rows",
                      label: `Rows and filters${
                        mode === "filtered"
                          ? ` (${criteria.criteria.length})`
                          : ""
                      }`,
                      contentRenderStrategy: "lazy",
                      content: (
                        /* Keyboard users can scroll the bounded active workspace. */
                        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
                        <div
                          ref={workspace === "rows" ? paneRef : undefined}
                          className="oms-export-workspace"
                          style={{ maxHeight: paneMaxHeight }}
                          role="region"
                          aria-label="Rows and filters settings"
                          tabIndex={0}
                        >
                          <SpaceBetween size="m">
                            <FormField
                              label="Rows to export"
                              constraintText="Dashboard filters do not apply."
                            >
                              <RadioGroup
                                value={mode}
                                items={[
                                  { value: "full", label: "All rows" },
                                  { value: "filtered", label: "Filtered rows" },
                                ]}
                                onChange={({ detail }) => {
                                  changed();
                                  const nextMode = detail.value as
                                    | "full"
                                    | "filtered";
                                  const target =
                                    nextMode === "full" ? full : filtered;
                                  if (
                                    target.jobId ||
                                    target.sourceReport.snapshotId !==
                                      source.snapshotId
                                  )
                                    target.editSettings(source);
                                  setMode(nextMode);
                                }}
                              />
                            </FormField>
                            {mode === "filtered" && (
                              <OMSRecordFilters
                                report={source}
                                state={editableCriteria}
                                embedded
                                focusRequested={focusFilters}
                                onFocusHandled={() => setFocusFilters(false)}
                              />
                            )}
                          </SpaceBetween>
                        </div>
                        /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
                      ),
                    },
                    ...(columnsEnabled
                      ? [
                          {
                            id: "columns",
                            label: `Columns (${
                              columns.strategy === "all"
                                ? source.columns.length
                                : columns.names.length
                            })`,
                            contentRenderStrategy: "lazy" as const,
                            content: (
                              /* Keyboard users can scroll the bounded active workspace. */
                              /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
                              <div
                                ref={
                                  workspace === "columns" ? paneRef : undefined
                                }
                                className="oms-export-workspace oms-export-workspace--columns"
                                style={{
                                  maxHeight: paneMaxHeight,
                                }}
                                role="region"
                                aria-label="Column settings"
                                tabIndex={0}
                              >
                                <OMSColumns
                                  report={source}
                                  settings={columns}
                                  onChange={(settings) => {
                                    changed();
                                    setColumns(settings);
                                  }}
                                  focusRequested={focusColumns}
                                  onFocusHandled={() => setFocusColumns(false)}
                                />
                              </div>
                              /* eslint-enable jsx-a11y/no-noninteractive-tabindex */
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
                {!columnsEnabled && columns.strategy === "selected" && (
                  <Alert type="warning">
                    New column selections are temporarily unavailable. Your
                    selection is preserved.
                    {previousRequest && " You can retry your previous request."}
                    <Button
                      onClick={() => {
                        changed();
                        setColumns({ ...columns, strategy: "all" });
                      }}
                    >
                      Use all columns
                    </Button>
                  </Alert>
                )}
              </>
            )}
            {active && accepted && (
              <SpaceBetween size="m">
                {(accepted?.csvFiles || 1) > 1 && (
                  <Box color="text-body-secondary">
                    This existing ZIP contains {accepted?.csvFiles} CSV files.
                    Edit settings to prepare one CSV.
                  </Box>
                )}
                {accepted.mode === "filtered" && (
                  <AppliedOmsFilters
                    report={source}
                    filters={accepted?.filters || criteria.filters}
                    operation={accepted?.operation || criteria.operation}
                    textCaseSensitive={accepted?.textCaseSensitive ?? true}
                    compact
                  />
                )}
                <ExpandableSection
                  variant="container"
                  headingTagOverride="h3"
                  headerText="Output columns"
                  headerDescription={columnSummary}
                >
                  <ol className="oms-report-download__applied-filters">
                    {(
                      accepted?.outputColumns ||
                      (columns.strategy === "all"
                        ? source.columns
                        : columns.names)
                    ).map((name) => (
                      <li key={name}>{name}</li>
                    ))}
                  </ol>
                </ExpandableSection>
              </SpaceBetween>
            )}
          </SpaceBetween>
        )}
      </div>
    </Modal>
  );
}
